import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const STEPS = [
  {
    number: '01',
    icon: 'ri-map-pin-2-line',
    title: 'Hindame objekti',
    desc: 'Tutvume objektiga ja kooskõlastame lahenduse juhtkonnaga.',
  },
  {
    number: '02',
    icon: 'ri-layout-2-line',
    title: 'Paigaldame sorteerimisala',
    desc: 'Paigaldame sobiva lahenduse — sorteerimisala või kaetud konteineri.',
  },
  {
    number: '03',
    icon: 'ri-recycle-line',
    title: 'Sorteerime ja käitleme',
    desc: 'Meie meeskond hoolitseb sorteerimise, logistika ja äraveo eest.',
  },
  {
    number: '04',
    icon: 'ri-file-list-3-line',
    title: 'Koostame aruanded',
    desc: 'Koostame jäätmekäitluse aktid ning üleandmisdokumendid.',
  },
];

const EXPAND_ITEMS = [
  { icon: 'ri-archive-line', text: 'Jäätmed kogutakse ja sorteeritakse liigiti big-bagidesse' },
  { icon: 'ri-truck-line', text: 'Äravedu toimub igapäevaselt või vastavalt vajadusele' },
  { icon: 'ri-file-text-line', text: 'Koostame jäätmekäitluse aktid ja aruanded' },
  { icon: 'ri-shake-hands-line', text: 'Lahendus kooskõlastatakse eelnevalt objekti juhtkonnaga' },
  { icon: 'ri-shield-check-line', text: 'Sorteerimisala on kaitstud ilmastiku eest' },
];

const HIGHLIGHTS = [
  {
    icon: 'ri-gift-line',
    title: 'Võimalik tasuta äravedu',
    desc: 'Teatud materjalid (nt puit ja metall) saab tasuta ära vedada.',
    accent: 'bg-pale-green',
    iconColor: 'text-eco-green',
  },
  {
    icon: 'ri-loop-right-line',
    title: 'Materjalide korduskasutus',
    desc: 'Kasutatavad materjalid leiavad rakenduse järgmistel objektidel.',
    accent: 'bg-[#F0F7F0]',
    iconColor: 'text-dark-green',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Läbipaistev aruandlus',
    desc: 'Saad täpse ülevaate käideldud jäätmete koguste ja liikide kohta.',
    accent: 'bg-[#F5FAF5]',
    iconColor: 'text-eco-green',
  },
];

export default function HowItWorks() {
  const [expanded, setExpanded] = useState(false);
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();
  const { ref: stepsRef, isVisible: stepsVisible } = useScrollAnimation();
  const { ref: pricingRef, isVisible: pricingVisible } = useScrollAnimation();
  const { ref: hlRef, isVisible: hlVisible } = useScrollAnimation();

  return (
    <section id="kuidas-tootab" className="py-28 bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-8">

        {/* ── Header ── */}
        <div
          ref={headRef}
          className={`text-center mb-16 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="inline-flex items-center gap-2 text-eco-green text-xs md:text-[42px] font-bold uppercase tracking-[0.2em] mb-4">
            <div className="w-6 h-0.5 bg-eco-green" />
            Kuidas meie teenus töötab
            <div className="w-6 h-0.5 bg-eco-green" />
          </span>
          <p className="mt-6 text-gray-500 text-base leading-relaxed max-w-2xl mx-auto">
            Paigaldame objektilt sõltuvalt sorteerimisala või kaetud konteineri, kus jäätmed
            kogutakse ja sorteeritakse liigiti. Meie meeskond hoolitseb sorteerimise, logistika
            ja äraveo eest vastavalt objekti vajadustele.
          </p>
        </div>

        {/* ── Steps ── */}
        <div
          ref={stepsRef}
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10 transition-all duration-700 ${stepsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {STEPS.map((step, i) => (
            <div
              key={step.number}
              className="bg-white rounded-2xl p-7 border border-gray-100 relative overflow-hidden group"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="absolute top-4 right-5 text-5xl font-black text-gray-50 select-none leading-none">
                {step.number}
              </div>
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-pale-green text-eco-green mb-5">
                <i className={`${step.icon} text-xl`} />
              </div>
              <h3 className="text-sm font-bold text-gray-900 mb-2">{step.title}</h3>
              <p className="text-gray-400 text-xs leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>

        {/* ── Expandable detail ── */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-16">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="w-full flex items-center justify-between px-8 py-5 cursor-pointer group"
          >
            <span className="text-sm font-bold text-gray-800 group-hover:text-eco-green transition-colors">
              Vaata täpsemalt
            </span>
            <div className={`w-8 h-8 flex items-center justify-center rounded-full bg-pale-green text-eco-green transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}>
              <i className="ri-arrow-down-s-line text-lg" />
            </div>
          </button>

          <div
            className={`transition-all duration-500 ease-in-out overflow-hidden ${expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
          >
            <div className="px-8 pb-7 border-t border-gray-50">
              <ul className="mt-5 space-y-3">
                {EXPAND_ITEMS.map((item) => (
                  <li key={item.text} className="flex items-start gap-4">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-pale-green text-eco-green flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-sm`} />
                    </div>
                    <span className="text-gray-600 text-sm leading-relaxed">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ── Highlights ── */}
        <div
          ref={hlRef}
          className={`grid grid-cols-1 md:grid-cols-3 gap-5 mb-16 transition-all duration-700 ${hlVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {HIGHLIGHTS.map((h, i) => (
            <div
              key={h.title}
              className={`${h.accent} rounded-2xl p-7 flex flex-col gap-4`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white">
                <i className={`${h.icon} text-xl ${h.iconColor}`} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">{h.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{h.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Pricing ── */}
        <div
          ref={pricingRef}
          className={`transition-all duration-700 ${pricingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="bg-dark-green rounded-3xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left text */}
              <div className="p-12 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 bg-eco-green/20 text-eco-green rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider mb-6 w-fit">
                  <i className="ri-price-tag-3-line text-xs" />
                  Hinnastamine
                </div>
                <h3 className="text-3xl font-extrabold text-white mb-4 leading-tight">
                  Maksate ainult tegeliku töö eest
                </h3>
                <p className="text-white/65 text-sm leading-relaxed mb-8 max-w-md">
                  Hinnastamine toimub vastavalt jäätmete mahule (m³) ja objekti eripärale.
                  Maksate ainult reaalselt käideldud jäätmete eest.
                </p>
                <a
                  href="#kontakt"
                  className="inline-flex items-center gap-2 bg-eco-green text-white font-bold text-sm px-6 py-3 rounded-xl cursor-pointer whitespace-nowrap hover:bg-[#3a9b4b] transition-colors w-fit"
                >
                  Küsi pakkumist
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
              {/* Right: features */}
              <div className="p-12 flex flex-col justify-center gap-4 lg:border-l border-white/10">
                {[
                  { icon: 'ri-scales-3-line', text: 'Hind sõltub käideldud jäätmete mahust (m³)' },
                  { icon: 'ri-building-line', text: 'Arvestame objekti eripäraga' },
                  { icon: 'ri-gift-2-line', text: 'Puit ja metall — sageli tasuta äravedu' },
                  { icon: 'ri-file-list-line', text: 'Läbipaistev arve koos aktidega' },
                ].map((item) => (
                  <div key={item.text} className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-eco-green/15 text-eco-green flex-shrink-0">
                      <i className={`${item.icon} text-base`} />
                    </div>
                    <span className="text-white/75 text-sm leading-relaxed pt-1">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
